import { useEffect, useState } from 'react';
import { useDateNow, useDispatch, useReduxState } from 'hooks';
import { format, subDays } from 'date-fns';
import api from 'api';
import { breakpoints } from 'styles';
import { FlexContainer, LoadingIndicator } from 'components/common';
import { Box } from 'components/common/box';
import { DateRangeSelector, LineChart } from 'components/common/charts';
import { RadioGroup } from 'components/common/form';
import { StatItem } from 'components/common/stats';
import { AnalyticsDeletedUsersWarning } from '.';

type AccessEntry = {
  labels: string[];
  deletedUsers: number[];
  validUsers: number[];
  totalCount: number;
};

const AnalyticsOverviewLogins = () => {
  const { dateNow } = useDateNow();

  const { dispatch } = useDispatch();

  const { messages, theme } = useReduxState();

  const [entries, setEntries] = useState<AccessEntry>(null);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(
    `${format(subDays(dateNow, 30), 'y-MM-dd')}T00:00:00`
  );
  const [toDate, setToDate] = useState(
    `${format(dateNow, 'y-MM-dd')}T23:59:59`
  );

  useEffect(() => {
    updateData();
  }, [includeDeleted, fromDate, toDate]);

  const handleDates = ({ fromDate, toDate }) => {
    setFromDate(fromDate);
    setToDate(toDate);
  };

  const updateData = async () => {
    try {
      setLoading(true);

      const entries = await api.get(
        `/admin/analytics/login` +
          `?fromDate=${fromDate}` +
          `&toDate=${toDate}` +
          `&includeDeleted=${includeDeleted}`
      );
      setEntries(entries.data.entries);
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    } finally {
      setLoading(false);
    }
  };

  const data = entries?.labels
    ? entries.labels.map((label, i) => ({
        label,
        [messages.analyticsUsersInTotal]:
          entries.validUsers[i] + entries.deletedUsers[i],
        [includeDeleted
          ? messages.analyticsExistingAccounts
          : messages.commonUsers]: entries.validUsers[i],
        [messages.analyticsDeletedAccounts]: entries.deletedUsers[i]
      }))
    : null;

  return (
    <div className="chart">
      <Box padding={0}>
        <div className="inner">
          <div className="header">
            <div className="total">
              <StatItem
                align="left"
                horizontal={true}
                label={messages.analyticsSignedInUsers}
                value={entries?.totalCount}
              />
            </div>

            <div className="controls">
              <DateRangeSelector updateData={handleDates} />
            </div>
          </div>

          <div className="options">
            <FlexContainer style={{ justifyContent: 'flex-end' }}>
              <RadioGroup
                field="includeDeleted"
                id="include-deleted"
                initialValue={includeDeleted}
                inline={true}
                onChange={(value) => setIncludeDeleted(value)}
                options={[
                  {
                    id: 'active',
                    label: messages.analyticsAccountsActive,
                    value: false
                  },
                  {
                    id: 'deleted',
                    label: messages.analyticsAccountsDeleted,
                    value: true
                  }
                ]}
              />
            </FlexContainer>

            <AnalyticsDeletedUsersWarning
              date={fromDate}
              style={{ margin: '15px 0' }}
              visible={includeDeleted}
            />
          </div>

          {loading ? (
            <LoadingIndicator style={{ padding: '130px 50px' }} />
          ) : (
            <LineChart
              data={data}
              xAxisDataKey="label"
              {...(includeDeleted
                ? {
                    lineColors: [
                      theme.chartMainColor,
                      theme.textColorLighter,
                      theme.dangerColor
                    ],
                    yAxisDataKey: [
                      messages.analyticsUsersInTotal,
                      messages.analyticsExistingAccounts,
                      messages.analyticsDeletedAccounts
                    ]
                  }
                : { yAxisDataKey: messages.commonUsers })}
            />
          )}
        </div>
      </Box>

      <style jsx>{`
        .inner {
          padding: 15px;
        }
        .header {
          padding: 5px 0;
        }
        .total {
          align-self: flex-start;
          flex-grow: 1;
          min-width: 0;
        }
        .controls {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }
        .options {
          padding: 10px 0;
        }
        @media (max-width: ${breakpoints.medium - 1}px) {
          .controls {
            margin-top: 15px;
          }
        }
        @media (min-width: ${breakpoints.medium}px) {
          .inner {
            padding: 25px;
          }
          .header {
            align-items: center;
            display: flex;
          }
        }
      `}</style>
    </div>
  );
};

export default AnalyticsOverviewLogins;
